import {
  Box,
  Card,
  Chip,
  Link,
  Stack,
  Table,
  Button,
  TableRow,
  TableBody,
  TableCell,
  TableHead,
  Typography,
  CircularProgress,
} from '@mui/material';

import { useRouter } from 'src/routes/hooks';

import { fDate } from 'src/utils/format-time';

import {
  SignerStatus,
  SigningStatus,
  SigningOriginType,
} from 'src/services/signing/signing.types';
import {
  useVoidSigningRequestMutation,
  useRemindSigningRequestMutation,
  useGetSigningRequestsForRecordQuery,
} from 'src/services/signing/signing.service';

import EmptyContent from 'src/components/empty-content';

// ----------------------------------------------------------------------

const STATUS_COLOR: Record<string, 'default' | 'info' | 'warning' | 'success' | 'error'> = {
  [SigningStatus.DRAFT]: 'default',
  [SigningStatus.SENT]: 'info',
  [SigningStatus.VIEWED]: 'info',
  [SigningStatus.IN_PROGRESS]: 'warning',
  [SigningStatus.COMPLETED]: 'success',
  [SigningStatus.DECLINED]: 'error',
  [SigningStatus.EXPIRED]: 'error',
  [SigningStatus.VOIDED]: 'error',
};

const ACTIVE_STATUSES = [SigningStatus.SENT, SigningStatus.VIEWED, SigningStatus.IN_PROGRESS];

interface Props {
  originType: SigningOriginType;
  originId: string;
}

export default function SigningDocumentsList({ originType, originId }: Props) {
  const router = useRouter();

  const { data: requests, isLoading } = useGetSigningRequestsForRecordQuery(
    {
      origin_entity_type: originType,
      origin_entity_id: originId,
    },
    // Signers complete on a separate (public) page, so refetch when this tab is (re)opened
    // to pick up status/completion changes made outside this store.
    { refetchOnMountOrArgChange: true }
  );

  const [remind, { isLoading: reminding }] = useRemindSigningRequestMutation();
  const [voidRequest, { isLoading: voiding }] = useVoidSigningRequestMutation();

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!requests?.length) {
    return <EmptyContent title="No signing requests yet" />;
  }

  return (
    <Card>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Document</TableCell>
            <TableCell>Sent</TableCell>
            <TableCell>Signed</TableCell>
            <TableCell>Status</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {requests.map((req) => {
            const total = req.signers?.length ?? 0;
            const signed = req.signers?.filter((s) => s.status === SignerStatus.SIGNED).length ?? 0;
            const isActive = ACTIVE_STATUSES.includes(req.status);
            return (
              <TableRow key={req.id}>
                <TableCell>
                  <Typography variant="body2">{req.name}</Typography>
                </TableCell>
                <TableCell>{req.sent_at ? fDate(req.sent_at) : '—'}</TableCell>
                <TableCell>{`${signed}/${total}`}</TableCell>
                <TableCell>
                  <Chip size="small" label={req.status} color={STATUS_COLOR[req.status] ?? 'default'} />
                </TableCell>
                <TableCell align="right">
                  <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center">
                    {isActive && (
                      <Button size="small" disabled={reminding} onClick={() => remind(req.id)}>
                        Remind
                      </Button>
                    )}
                    {req.completed_file?.public_path && (
                      <Button size="small" component={Link} href={req.completed_file.public_path} target="_blank">
                        View
                      </Button>
                    )}
                    {req.status === SigningStatus.DRAFT && (
                      <Button size="small" onClick={() => router.push(`/dashboard/signing/${req.id}/prepare`)}>
                        Edit
                      </Button>
                    )}
                    {(req.status === SigningStatus.DRAFT || isActive) && (
                      <Button size="small" color="error" disabled={voiding} onClick={() => voidRequest(req.id)}>
                        Void
                      </Button>
                    )}
                  </Stack>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Card>
  );
}
